import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useMatch } from 'react-router-dom';
import { setAllShapes, clearSelection } from '../../../../../store/slices/drawingSlice';
import { setSaveStatus }            from '../../../../../store/slices/navigationSlice';
import { saveDrawingShapes, loadDrawingShapes } from './drawingService';
import { shapesToGeoJSON, emptyCollection } from './drawingUtils';
import { useProjectHeader } from '../../../../../pages/buildProject/Helpers/pageDiv/ProjectHeaderContext';

export const SOURCES = {
  shapes:  'drawing-shapes',
  preview: 'drawing-preview',
};

export const LAYERS = {
  fill:           'drawing-fill',
  stroke:         'drawing-stroke',
  selectedFill:   'drawing-selected-fill',
  selectedStroke: 'drawing-selected-stroke',
  vertices:       'drawing-vertices',
  edgeLabels:     'drawing-edge-labels',
  previewLine:    'drawing-preview-line',
  previewFill:    'drawing-preview-fill',
  previewVertex:  'drawing-preview-vertex',
  previewEdge:    'drawing-preview-edge', 
};

const prop = (name) => ['get', name];
 
const MAX_Z_LAYERS = 50;

export const drawingSourceRef = {
  setShapesData:  null,
  setShapesOnly:  null,
  setShapesFull:  null,
  setPreviewData: null,
  isDragging:     false,
  onInit:         null,
  hitLayers:      [],
};

export default function DrawingLayer() {
  const map = useSelector((s) => s.map.mapContainer); 
  const initialised = useRef(false);
 
  useEffect(() => { 
    if (!map) return;

    const init = () => {
      if (initialised.current) return; 
      map.addSource(SOURCES.shapes, {
        type:      'geojson',
        data:      emptyCollection(),
        promoteId: 'id',
      });
      map.addSource(SOURCES.preview, {
        type: 'geojson',
        data: emptyCollection(),
      });
 
      const zLayerIds = [];
      for (let z = 0; z < MAX_Z_LAYERS; z++) {
        const zFilter = ['==', ['get', 'zIndex'], z];
        const fillId  = `${LAYERS.fill}-${z}`;
        const strokeId = `${LAYERS.stroke}-${z}`;

        map.addLayer({ id: fillId, type: 'fill', source: SOURCES.shapes,
          filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['!', ['has', 'vertexIndex']], ['!', ['has', 'edgeIndex']], zFilter],
          paint: { 'fill-color': prop('fillColor'), 'fill-opacity': ['coalesce', prop('opacity'), 1], 'fill-antialias': false },
        });
        map.addLayer({ id: strokeId, type: 'line', source: SOURCES.shapes,
          filter: ['all', ['!=', ['geometry-type'], 'Point'], ['!', ['has', 'edgeIndex']], ['!', ['has', 'vertexIndex']], zFilter],
          paint: { 'line-color': prop('strokeColor'), 'line-width': prop('strokeWidth'), 'line-opacity': ['coalesce', prop('opacity'), 1] },
        });
        zLayerIds.push(fillId, strokeId);
      }
      drawingSourceRef.hitLayers = zLayerIds;

      map.addLayer({ id: LAYERS.selectedFill, type: 'fill', source: SOURCES.shapes,
        filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', prop('selected'), true]],
        paint: { 'fill-color': 'rgba(255, 200, 0, 0)', 'fill-opacity': 1, 'fill-antialias': false },
      });
      map.addLayer({ id: LAYERS.selectedStroke, type: 'line', source: SOURCES.shapes,
        filter: ['all', ['!=', ['geometry-type'], 'Point'], ['==', prop('selected'), true], ['!', ['has', 'edgeIndex']], ['!', ['has', 'vertexIndex']]],
        paint: { 'line-color': 'rgba(245, 165, 35, 0)', 'line-width': ['+', prop('strokeWidth'), 2], 'line-dasharray': [4, 2] },
      });

      map.addLayer({ id: LAYERS.previewFill, type: 'fill', source: SOURCES.preview,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': '#1a73e8', 'fill-opacity': 0.10, 'fill-antialias': false },
      });
      map.addLayer({ id: LAYERS.previewLine, type: 'line', source: SOURCES.preview,
        filter: ['!=', ['geometry-type'], 'Point'],
        paint: { 'line-color': '#1a73e8', 'line-width': 2, 'line-dasharray': [4, 3], 'line-opacity': 0.9 },
      });

      map.addLayer({ id: LAYERS.vertices, type: 'circle', source: SOURCES.shapes,
        filter: ['all', ['has', 'vertexIndex'], ['==', prop('selected'), true]],
        paint: { 'circle-radius': 5, 'circle-color': '#3b5bdb', 'circle-stroke-width': 1.5, 'circle-stroke-color': '#ffffff' },
      });
      map.addLayer({ id: LAYERS.edgeLabels, type: 'symbol', source: SOURCES.shapes,
        filter: ['all', ['has', 'edgeIndex'], ['==', prop('selected'), true]],
        layout: { 'text-field': prop('label'), 'text-font': ['Open Sans Regular'], 'text-size': 11, 'text-allow-overlap': true, 'text-ignore-placement': true, 'text-anchor': 'center' },
        paint: { 'text-color': '#222222', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      });
      map.addLayer({ id: LAYERS.previewVertex, type: 'circle', source: SOURCES.preview,
        filter: ['==', prop('vertexPreview'), true],
        paint: { 'circle-radius': 5, 'circle-color': ['case', ['==', prop('isFirst'), true], '#e03131', '#3b5bdb'], 'circle-stroke-width': 1.5, 'circle-stroke-color': '#ffffff' },
      });
      map.addLayer({ id: LAYERS.previewEdge, type: 'symbol', source: SOURCES.preview,
        filter: ['==', prop('edgeLabel'), true],
        layout: { 'text-field': prop('label'), 'text-font': ['Open Sans Regular'], 'text-size': 11, 'text-allow-overlap': true, 'text-ignore-placement': true, 'text-anchor': 'center' },
        paint: { 'text-color': '#222222', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      });
 
      const setShapesSource = (shapes, selectedIds) => {
        map.getSource(SOURCES.shapes)?.setData(shapesToGeoJSON(shapes, selectedIds));
      };
      drawingSourceRef.setShapesData  = setShapesSource;
      drawingSourceRef.setShapesOnly  = setShapesSource; 
      drawingSourceRef.setShapesFull  = setShapesSource;
      drawingSourceRef.setPreviewData = (geojson) => {
        map.getSource(SOURCES.preview)?.setData(geojson);
      };
 
      initialised.current = true;
      drawingSourceRef.onInit?.();
    };

    const handleStyleData = () => { 
      if (!map.isStyleLoaded()) return;
      if (map.getSource(SOURCES.shapes)) return; 

      initialised.current = false; 
      drawingSourceRef.setShapesData  = null;
      drawingSourceRef.setShapesOnly  = null;
      drawingSourceRef.setShapesFull  = null;
      drawingSourceRef.setPreviewData = null;
      init();
    };

    if (map.isStyleLoaded()) init();
    else map.once('load', init);
    map.on('styledata', handleStyleData);

    return () => {
      map.off('styledata', handleStyleData);
      drawingSourceRef.setShapesData  = null;
      drawingSourceRef.setShapesOnly  = null;
      drawingSourceRef.setShapesFull  = null;
      drawingSourceRef.setPreviewData = null;
      initialised.current = false;
 
      if (!map.style) return;

      for (let z = 0; z < MAX_Z_LAYERS; z++) {
        try { if (map.getLayer(`${LAYERS.fill}-${z}`))   map.removeLayer(`${LAYERS.fill}-${z}`);   } catch (e) { console.warn(e); }
        try { if (map.getLayer(`${LAYERS.stroke}-${z}`)) map.removeLayer(`${LAYERS.stroke}-${z}`); } catch (e) { console.warn(e); }
      }
      Object.values(LAYERS).forEach((l) => {
        try { if (map.getLayer(l))  map.removeLayer(l);  } catch (e) { console.warn(e); }
      });
      Object.values(SOURCES).forEach((s) => {
        try { if (map.getSource(s)) map.removeSource(s); } catch (e) { console.warn(e); }
      });
    };
  }, [map]);

  return null;
}
 
const DRW_LS_KEY    = (floorId) => `drawing-shapes-floor-${floorId}`;
const DRW_MAX_RETRY = 1;
const DRW_RETRY_MS  = 2000;

export function DrawingAutoSave() {
  const dispatch     = useDispatch();
  const shapes       = useSelector((s) => s.drawing.shapes);
  const currentFloor = useSelector((s) => s.api.currentFloor);
  const projectData  = useSelector((s) => s.api.projectData);
  const floorId      = currentFloor?.enc_id ?? null;
  const projectId    = projectData?.enc_id ?? null;

  const { handleEnableDisable } = useProjectHeader();

  const baselineRef  = useRef(null);
  const saveTimer    = useRef(null);
  const statusTimer  = useRef(null);
  const saveVersion  = useRef(0);

  const floorShapes = useMemo(
    () => shapes.filter((s) => s.properties?.floorId === floorId),
    [shapes, floorId],
  );

  const markSaved = () => {
    dispatch(setSaveStatus('saved'));
    if (statusTimer.current) clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => dispatch(setSaveStatus('idle')), 3000);
  };

  const attemptSave = (targetFloorId, snapshot, serialized, version, attempt = 0) => {
    if (version !== saveVersion.current) return;

    saveDrawingShapes(targetFloorId, snapshot)
      .then((result) => {
        if (version !== saveVersion.current) return;
        if (result.type === 1) {
          baselineRef.current = serialized;
          markSaved();
          handleEnableDisable();
        } else {
          throw new Error(result.errormessage || 'Save failed');
        }
      })
      .catch(() => {
        if (version !== saveVersion.current) return;
        if (attempt < DRW_MAX_RETRY - 1) {
          setTimeout(
            () => attemptSave(targetFloorId, snapshot, serialized, version, attempt + 1),
            DRW_RETRY_MS * Math.pow(2, attempt),
          );
        } else {
          try { localStorage.setItem(DRW_LS_KEY(targetFloorId), JSON.stringify(snapshot)); } catch {}
          dispatch(setSaveStatus('failed'));
        }
      });
  };

  useEffect(() => {
    if (!floorId) {
      baselineRef.current = null;
      return;
    }

    baselineRef.current = null;
    dispatch(setSaveStatus('idle'));

    const load = async () => {
      try {
        let savedShapes = await loadDrawingShapes(floorId);

        if (savedShapes?.length > 0) {
          savedShapes = savedShapes.map((s) => ({
            ...s,
            properties: { ...(s.properties || {}), floorId },
          }));
        }

        const normalized = savedShapes?.length > 0 ? savedShapes : [];
        baselineRef.current = JSON.stringify(normalized);
        dispatch(setAllShapes(normalized));
      } catch {
        baselineRef.current = JSON.stringify([]);
        dispatch(setAllShapes([]));
      }
    };

    load();

    return () => {
      if (saveTimer.current)  clearTimeout(saveTimer.current);
      if (statusTimer.current) clearTimeout(statusTimer.current);
    };
  }, [floorId, dispatch]);

  useEffect(() => {
    if (baselineRef.current === null) return;

    const serialized = JSON.stringify(floorShapes);
    if (serialized === baselineRef.current) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);

    const snapshot = floorShapes;

    saveTimer.current = setTimeout(() => {
      saveVersion.current += 1;
      const version = saveVersion.current;

      try { localStorage.setItem(DRW_LS_KEY(floorId), JSON.stringify(snapshot)); } catch {}

      dispatch(setSaveStatus('saving'));
      attemptSave(floorId, snapshot, serialized, version);
    }, 1000);
  }, [floorShapes, floorId]);

  return null;
}
 
export function DrawingSync() {
  const dispatch     = useDispatch();
  const shapes       = useSelector((s) => s.drawing.shapes);
  const selectedIds  = useSelector((s) => s.drawing.selectedIds);
  const inProgress   = useSelector((s) => s.drawing.inProgress);
  const currentFloor = useSelector((s) => s.api.currentFloor);
  // const { pathname } = useLocation();
  const isFloorplan  = !!useMatch('/project/:id/floor-plan/:subid');

  const floorId = currentFloor?.enc_id ?? null;
 
  useEffect(() => {
    // const isFloorPlanPage = /\/project\/[^/]+\/floor-plan\/[^/]+/.test(pathname);
    if (!isFloorplan) {
      dispatch(clearSelection());
    }
  }, [isFloorplan, dispatch]);
 
  const floorShapes = useMemo(
    () => shapes.filter((s) => s.properties?.floorId === floorId),
    [shapes, floorId],
  );
  const floorSelectedIds = useMemo(
    () => selectedIds.filter((id) => floorShapes.some((s) => String(s.id) === id)),
    [selectedIds, floorShapes],
  );
 
  const [initCount, setInitCount] = useState(0);
  useEffect(() => {
    drawingSourceRef.onInit = () => setInitCount((c) => c + 1);
    return () => { drawingSourceRef.onInit = null; };
  }, []);

  useEffect(() => {
    if (drawingSourceRef.isDragging) return; 
    drawingSourceRef.setShapesData?.(floorShapes, floorSelectedIds);
  }, [floorShapes, floorSelectedIds, initCount]);
 
  useEffect(() => {
    dispatch(clearSelection());
  }, [floorId, dispatch]);

  useEffect(() => {
    if (!inProgress) {
      drawingSourceRef.setPreviewData?.(emptyCollection());
    }
  }, [inProgress]);

  return null;
}